import type { Dispatch } from 'react';
import type { Reducer } from 'redux';

import { getUserAccountTypeForClientId } from 'src/data';
import { getAccount } from 'src/services/api';
import type { StoreProps } from 'src/store';
import { noop } from 'src/utils';
import { action } from 'src/utils/redux';
import type { ArrayElement } from 'src/utils/types';

export const actions = [
  action('VERIFY_LOGIN_REQUEST' as const).payload<unknown>(),
  action('VERIFY_LOGIN_SUCCESS' as const).payload<LoginDetails>(),
  action('VERIFY_LOGIN_FAIL' as const).payload<{ code: number }>(),
  action('REQUEST_PAYMENT_SUCCESS' as const).payload<unknown>(),
];

export type Action = ArrayElement<typeof actions>;

export const getAccountData = () => (dispatch: Dispatch<Action>) => {
  dispatch({
    type: 'VERIFY_LOGIN_REQUEST',
    payload: {},
  });
  return getAccount()
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: 'VERIFY_LOGIN_SUCCESS',
          payload: res,
        });
      } else {
        dispatch({
          type: 'VERIFY_LOGIN_FAIL',
          payload: { code: res.status },
        });
      }
    })
    .catch((err) => {
      dispatch({
        type: 'VERIFY_LOGIN_FAIL',
        payload: err,
      });
    });
};

export const getAccountFetchStatus =
  (state: StoreProps) => (fetchFunction: () => void) => {
    const hasLoaded = Boolean(state.fetchingFlags.verifyLoginComplete);
    const isFetching = Boolean(state.fetchingFlags.verifyLoginFetching);
    const wasFetched = hasLoaded && state.account.loginVerified;
    return {
      fetch: hasLoaded || isFetching ? noop : fetchFunction,
      hasLoaded,
      wasFetched,
    };
  };

const reducer: Reducer<LoginDetails, Action> = (
  state = {} as LoginDetails,
  act,
) => {
  switch (act.type) {
    case 'VERIFY_LOGIN_SUCCESS':
      return {
        ...state,
        ...act.payload,
        loginVerified: true,
      };
    case 'VERIFY_LOGIN_FAIL':
      return {
        ...state,
        ...act.payload,
        loginVerified: false,
      };
    case 'REQUEST_PAYMENT_SUCCESS':
      return {
        ...state,
        paid: true,
      };
    default:
      return state;
  }
};

export default reducer;

// The shape of this object is incorrect. If a buyer invokes this API then none
// of these fields will be present. This is also invalidated by the default store
// state -- an empty object.
// export interface LoginDetails {
//   emailAddress?: string;
//   id?: number;
//   clientTypeId?: number;
//   businessName?: string;
//   firstName?: string;
//   powerPack?: boolean;
//   paid?: boolean;
//   clientNumber?: number;
//   loginVerified?: boolean;
//   loadedAccountListings?: boolean;
//   code?: number;
// }

export interface LoginDetails {
  emailAddress: string;
  id: number;
  clientTypeId: number;
  businessName: string;
  firstName: string;
  powerPack: boolean;
  paid: boolean;
  clientNumber: number;
  loginVerified: boolean;
  // TODO: remove
  loadedAccountListings: boolean;
  code?: number;
}

// This has been modelled as a selector to colocate this funky business logic.
// This will make replacing this easier in the future
export const selectUserAccountType = (store: StoreProps) =>
  getUserAccountTypeForClientId(store.account.clientTypeId);
